import { Repeat, XCircle, CheckCircle2, AlertTriangle, BookCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  }).format(Number(n ?? 0));
}

/** Amounts arrive as numbers (r2) or preformatted "123.45€" strings. */
function fmtAmount(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return formatEur(v);
  return v;
}

const FREQUENCY_LABELS = {
  monthly: 'Mensual', mensual: 'Mensual',
  quarterly: 'Trimestral', trimestral: 'Trimestral',
  yearly: 'Anual', annual: 'Anual', anual: 'Anual',
  weekly: 'Semanal', semanal: 'Semanal',
};
function frequencyLabel(f) {
  if (!f) return '';
  return FREQUENCY_LABELS[String(f).toLowerCase()] ?? f;
}

/**
 * ReplayClosingPreview — rich two-step approval card for REPLAY_CLOSING.
 *
 * Materializes pending recurring-template journal entries up to a target month.
 * The generic PendingActionCard drops the nested `plantillas` array, so the user
 * could not see which templates would fire nor how many entries would be created
 * before confirming. This card surfaces the per-template table (name, frequency,
 * next due date, entries to generate, balance check) plus the batch totals.
 *
 * Backend preview shape (raw keys via toolExecutor `_raw`):
 *   { year, mes, as_of, total_plantillas, total_asientos,
 *     plantillas: [{ nombre, frecuencia, proximo_vencimiento, asientos_a_generar,
 *                    cuadrado, total_debe, total_haber, fin_alcanzado? }] }
 *
 * Props: action { type, preview }, onApprove, onReject, status
 */
export default function ReplayClosingPreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <BookCheck className="w-3.5 h-3.5" />
        <span>Cierre aprobado — materializando asientos…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Reaplicación de cierre cancelada</span>
      </div>
    );
  }

  const plantillas = preview?.plantillas ?? [];
  const mes = preview?.mes;
  const year = preview?.year;
  const periodo = mes != null && year != null
    ? `${String(mes).padStart(2, '0')}/${year}`
    : null;
  const totalAsientos = Number(preview?.total_asientos ?? 0);
  const totalPlantillas = Number(preview?.total_plantillas ?? plantillas.length);
  const nada = totalAsientos <= 0;

  return (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/20 p-4 space-y-3 my-2 max-w-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Badge variant="outline" className="text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 text-[10px] mb-1">
            Requiere aprobación
          </Badge>
          <p className="text-sm font-semibold text-foreground leading-tight flex items-center gap-1">
            <Repeat className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 shrink-0" />
            Reaplicar cierre{periodo ? ` hasta ${periodo}` : ''}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {totalAsientos} asiento{totalAsientos !== 1 ? 's' : ''} desde {totalPlantillas} plantilla{totalPlantillas !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Templates table */}
      {plantillas.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-indigo-200 dark:border-indigo-800 text-muted-foreground">
                <th className="text-left pb-1 font-medium">Plantilla</th>
                <th className="text-left pb-1 font-medium">Frecuencia</th>
                <th className="text-right pb-1 font-medium">Asientos</th>
                <th className="text-right pb-1 font-medium">Importe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-indigo-100 dark:divide-indigo-900/40">
              {plantillas.map((t, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2 text-foreground max-w-[120px] truncate">
                    <span className="flex items-center gap-1">
                      {t.nombre || '—'}
                      {t.cuadrado === false && (
                        <AlertTriangle className="w-2.5 h-2.5 text-warning shrink-0" aria-label="Descuadrado" />
                      )}
                    </span>
                    {t.proximo_vencimiento && (
                      <span className="block text-[9px] text-muted-foreground">vence {t.proximo_vencimiento}</span>
                    )}
                    {t.fin_alcanzado && (
                      <span className="block text-[9px] text-warning">fin alcanzado</span>
                    )}
                  </td>
                  <td className="py-1 pr-2 text-muted-foreground">{frequencyLabel(t.frecuencia)}</td>
                  <td className="py-1 text-right tabular-nums text-foreground">{t.asientos_a_generar ?? 0}</td>
                  <td className="py-1 text-right tabular-nums text-muted-foreground">{fmtAmount(t.total_debe)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5 text-success" />
          No hay plantillas pendientes de materializar.
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onReject} className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" />Cancelar
        </Button>
        <Button size="sm" onClick={() => onApprove()} disabled={nada}
          className="flex-1 h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">
          <BookCheck className="w-3 h-3 mr-1" />Materializar
        </Button>
      </div>
    </div>
  );
}
